import path from 'path'
import fs from 'fs/promises'
import { existsSync } from 'fs'
import { spawn } from 'child_process'
import { AgentProject } from '../agent-project/agent-project.js'
import { auth } from '../auth/index.js'
import { pascalCase } from '../utils/strings.js'
import { BUILTIN_INTERFACES } from '../constants.js'
import { getBpCli } from '../commands/bp-cli.js'

export interface InterfaceInfo {
  alias: string
  name: string
  version: string
  fullVersion: string // e.g., "translator@1.0.0"
}

export interface InterfaceSyncResult {
  synced: InterfaceInfo[]
  errors: Array<{ alias: string; error: string }>
}

export class InterfaceSync {
  private projectPath: string
  private botProjectPath: string
  private bpModulesPath: string

  constructor(projectPath: string, botProjectPath: string) {
    this.projectPath = projectPath
    this.botProjectPath = botProjectPath
    this.bpModulesPath = path.join(botProjectPath, 'bp_modules')
  }

  /**
   * Get built-in interfaces (hard-coded constants)
   */
  private async parseInterfaces(): Promise<InterfaceInfo[]> {
    const interfaces: InterfaceInfo[] = []

    for (const [alias, fullVersion] of Object.entries(BUILTIN_INTERFACES)) {
      const parts = fullVersion.split('@')
      const name = parts[0] || ''
      const version = parts[1] || 'latest'

      interfaces.push({
        alias,
        name,
        version,
        fullVersion,
      })
    }

    return interfaces
  }

  /**
   * Check if an interface is already installed with the correct version
   */
  private async isInterfaceSynced(interfaceInfo: InterfaceInfo): Promise<boolean> {
    const targetFolder = path.join(this.bpModulesPath, `interface_${pascalCase(interfaceInfo.alias)}`)

    if (!existsSync(targetFolder)) {
      return false
    }

    // Check if the index.ts has the correct version
    try {
      const indexPath = path.join(targetFolder, 'index.ts')
      if (!existsSync(indexPath)) {
        return false
      }

      const indexContent = await fs.readFile(indexPath, 'utf-8')

      // Look for version in the format: version: "1.0.0",
      const versionMatch = indexContent.match(/version:\s*["']([^"']+)["']/)
      if (!versionMatch) {
        return false
      }

      const installedVersion = versionMatch[1]
      return installedVersion === interfaceInfo.version
    } catch {
      return false
    }
  }

  /**
   * Install an interface using bp CLI
   */
  private async installInterface(interfaceInfo: InterfaceInfo): Promise<void> {
    // Get credentials
    const credentials = await auth.getActiveCredentials()
    const project = await AgentProject.load(this.projectPath)
    const workspaceId = project.agentInfo?.workspaceId || credentials.workspaceId

    if (!workspaceId) {
      throw new Error('No workspace ID found. Please login with "adk login"')
    }

    // Get the bp CLI path (bundled with ADK)
    const bpCli = await getBpCli(this.projectPath)
    const bpPath = bpCli.path

    return new Promise((resolve, reject) => {
      const args = [
        'add',
        `interface:${interfaceInfo.fullVersion}`,
        '-y',
        '--installPath',
        this.botProjectPath,
        '--token',
        credentials.token,
        '--workspaceId',
        workspaceId,
        '--apiUrl',
        credentials.apiUrl,
      ]

      // On Windows, .js files need to be executed with node explicitly
      const isJsFile = bpPath.endsWith('.js')
      const command = isJsFile ? 'node' : bpPath
      const commandArgs = isJsFile ? [bpPath, ...args] : args

      const child = spawn(command, commandArgs, {
        cwd: this.projectPath,
        stdio: 'pipe',
      })

      let stdout = ''
      let stderr = ''

      child.stdout?.on('data', (data) => {
        stdout += data.toString()
      })

      child.stderr?.on('data', (data) => {
        stderr += data.toString()
      })

      child.on('close', (code) => {
        if (code === 0) {
          resolve()
        } else {
          reject(new Error(`Failed to install interface ${interfaceInfo.fullVersion}: ${stderr || stdout}`))
        }
      })

      child.on('error', (error) => {
        reject(new Error(`Failed to spawn bp command: ${error.message}`))
      })
    })
  }

  /**
   * Rename interface folder from bp_modules/{name} to bp_modules/interface_{PascalCaseAlias}
   */
  private async renameInterfaceFolder(interfaceInfo: InterfaceInfo): Promise<void> {
    const sourceFolder = path.join(this.bpModulesPath, interfaceInfo.name)
    const targetFolder = path.join(this.bpModulesPath, `interface_${pascalCase(interfaceInfo.alias)}`)

    if (!existsSync(sourceFolder)) {
      throw new Error(`Interface folder not found: ${sourceFolder}`)
    }

    // Remove target folder if it exists
    if (existsSync(targetFolder)) {
      await fs.rm(targetFolder, { recursive: true, force: true })
    }

    // Rename source to target
    await fs.rename(sourceFolder, targetFolder)
  }

  /**
   * Remove existing interface folder
   */
  private async removeInterfaceFolder(alias: string): Promise<void> {
    const targetFolder = path.join(this.bpModulesPath, `interface_${pascalCase(alias)}`)

    if (existsSync(targetFolder)) {
      await fs.rm(targetFolder, { recursive: true, force: true })
    }
  }

  /**
   * Sync all interfaces
   */
  async syncInterfaces(): Promise<InterfaceSyncResult> {
    const interfaces = await this.parseInterfaces()
    const synced: InterfaceInfo[] = []
    const errors: Array<{ alias: string; error: string }> = []

    if (interfaces.length === 0) {
      return { synced, errors }
    }

    // Ensure bp_modules directory exists
    await fs.mkdir(this.bpModulesPath, { recursive: true })

    for (const interfaceInfo of interfaces) {
      try {
        const isAlreadySynced = await this.isInterfaceSynced(interfaceInfo)

        if (isAlreadySynced) {
          synced.push(interfaceInfo)
          continue
        }

        // Remove existing folder if version mismatch
        await this.removeInterfaceFolder(interfaceInfo.alias)

        // Install the interface
        await this.installInterface(interfaceInfo)

        // Rename to our controlled name
        await this.renameInterfaceFolder(interfaceInfo)

        synced.push(interfaceInfo)
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : String(error)
        errors.push({ alias: interfaceInfo.alias, error: errorMsg })
      }
    }

    return { synced, errors }
  }
}
